import { updateTransaction } from '../graphql/mutations';
import { UpdateTransactionInput, Transaction } from '../models/GQL_API';
import { useDispatch, useSelector } from 'react-redux';
import {
  setListing,
  setSelected,
  setNextToken,
} from '../store/ducks/transaction';
import {
  HeadCell,
  ListingVariables,
  transactionGetVariables,
  transactionUpdateVariables,
} from '../models/app';
import useApp from './useApp';
import {
  getTransaction,
  listTransactions,
  transactionsByEvent,
} from '../graphql/queries';
import { onCreateTransaction } from '../graphql/subscriptions';
import { generateClient } from '@aws-amplify/api';
import { ModelSortDirection } from '../models/GQL_API';
import { toDayRangeIso } from '../helpers/dateRange';

const client = generateClient();

/**
 * One page of transactions. With an event, read only that event's partition
 * of the transactionsByEvent GSI (createdAt sort key = date range key
 * condition); without one fall back to the table scan.
 */
const fetchTransactionsPage = async (
  eventId: string | undefined,
  createdAt: { between: string[] } | undefined,
  filter: any,
  nextToken: string | null | undefined,
): Promise<{ items: any[]; nextToken: string | null | undefined }> => {
  if (eventId) {
    const variables: any = {
      eventId,
      filter: Object.keys(filter).length > 0 ? filter : undefined,
      limit: 1000,
      nextToken,
      sortDirection: ModelSortDirection.DESC,
    };
    if (createdAt) variables.createdAt = createdAt;
    const result: any = await client.graphql({
      query: transactionsByEvent,
      variables,
      authMode: 'userPool',
    });
    return {
      items: result.data.transactionsByEvent.items ?? [],
      nextToken: result.data.transactionsByEvent.nextToken,
    };
  }
  const scanFilter = createdAt
    ? { ...filter, createdAt: { between: createdAt.between } }
    : filter;
  const result: any = await client.graphql<Transaction>({
    query: listTransactions,
    variables: { filter: scanFilter, limit: 1000, nextToken },
    authMode: 'userPool',
  });
  return {
    items: result.data.listTransactions.items ?? [],
    nextToken: result.data.listTransactions.nextToken,
  };
};

/** Whole local days [fromDate 00:00, toDate 23:59:59.999]. */
const buildCreatedAtRange = (
  fromDateValue?: string,
  toDateValue?: string,
): { between: string[] } | undefined => {
  const range = toDayRangeIso(fromDateValue, toDateValue);
  return range ? { between: range } : undefined;
};
const useTransaction = (listingName: string, singleName: string) => {
  const dispatch = useDispatch();
  const { showConfirm, showError } = useApp();

  const nextToken = useSelector(
    (state: any) => state[`${listingName}`]['nextToken'],
  );

  const transactionsListing = useSelector(
    (state: any) => state[`${listingName}`]['listing'],
  );
  // Page (Redux) filters: export calls fetchAll without generalFilters.
  const pageGeneralFilters = useSelector((state: any) => state.generalFilters);
  async function fetch(params: ListingVariables) {
    try {
      const { searchText, generalFilters } = params;
      const filter: any = {};
      filter.or = [];
      filter.and = [];
      if (searchText.length > 0) {
        filter.or.push({ guestName: { contains: searchText.toLowerCase() } });
        filter.or.push({
          guestPhone_number: { contains: searchText.toLowerCase() },
        });
      }
      const createdAt = buildCreatedAtRange(
        generalFilters?.fromDate,
        generalFilters?.toDate,
      );

      if (filter.and && filter.and.length === 0) {
        delete filter.and;
      }
      if (filter.or && filter.or.length === 0) {
        delete filter.or;
      }

      // Event-scoped page: the page's event drives the index query.
      const page = await fetchTransactionsPage(
        generalFilters?.event || undefined,
        createdAt,
        filter,
        nextToken,
      );
      const currentNextToken = page.nextToken;
      const responseListing = page.items;
      // Filters changed while this page was loading: drop the old response.
      if (params.isStale?.()) return [];

      let listing = [...transactionsListing, ...responseListing];
      dispatch(setListing(listing));
      dispatch(setNextToken(currentNextToken));
      return listing;
    } catch (err: Error | any) {
      showError(err.message || err);
      return [];
    }
  }

  async function fetchAll(params: ListingVariables) {
    try {
      const { searchText, generalFilters } = params;
      let allListing: any[] = [];
      const filter: any = {};
      filter.or = [];
      filter.and = [];
      if (searchText.length > 0) {
        // Transaction has guestName / guestPhone_number (no name/phone_number).
        filter.or.push({ guestName: { contains: searchText.toLowerCase() } });
        filter.or.push({
          guestPhone_number: { contains: searchText.toLowerCase() },
        });
      }
      const createdAt = buildCreatedAtRange(
        generalFilters?.fromDate,
        generalFilters?.toDate,
      );

      if (filter.and && filter.and.length === 0) {
        delete filter.and;
      }
      if (filter.or && filter.or.length === 0) {
        delete filter.or;
      }

      const eventId: string | undefined =
        generalFilters?.event || pageGeneralFilters?.event || undefined;
      let currentNextToken: string | null | undefined = undefined;
      do {
        const page = await fetchTransactionsPage(
          eventId,
          createdAt,
          filter,
          currentNextToken,
        );
        allListing = [...allListing, ...page.items];
        currentNextToken = page.nextToken;
      } while (currentNextToken);
      return allListing;
    } catch (err: Error | any) {
      showError(err.message || err);
      return [];
    }
  }

  async function get(params: transactionGetVariables) {
    try {
      const { id, listing } = params;
      let single: Transaction | undefined;
      if (listing?.length) {
        single = listing.find((resource: any) => resource.id === id);
      }
      if (single === undefined) {
        const listing: any = await client.graphql<Transaction>({
          query: getTransaction,
          variables: { id },
          authMode: 'userPool',
        });
        single = listing.data.getTransaction;
      }
      return single;
    } catch (err) {
      showError(err);
    }
  }

  async function update(params: transactionUpdateVariables) {
    try {
      const { data } = params;
      const original: any = await get(params);
      if (!original) {
        showError(`Invalid ${singleName} ID`);
        return;
      }

      const updateInput: UpdateTransactionInput = {
        id: original.id,
        refunded_amount_cents: data.refunded_amount_cents
          ? data.refunded_amount_cents
          : original!.refunded_amount_cents,
        _version: original._version,
      };

      let updatedTransaction = await client.graphql({
        query: updateTransaction,
        variables: { input: updateInput },
        authMode: 'userPool',
      });

      showConfirm(`${singleName} has been updated successfully`);
      return updatedTransaction.data.updateTransaction;
    } catch (err) {
      showError(err);
    }
  }

  async function exportAll(params: any) {
    try {
      let exportedData: Transaction[] = [];
      const data: Transaction[] = await fetchAll(params);
      for (let user of data!) {
        let row: Transaction = { ...user };
        exportedData.push(row);
      }
      return exportedData;
    } catch (err: any) {
      showError(err);
    }
  }

  const headCells: readonly HeadCell[] = [
    {
      id: 'transactionID',
      numeric: false,
      disablePadding: false,
      label: 'Trx ID',
    },

    {
      id: 'guestName',
      numeric: false,
      disablePadding: false,
      label: 'Guest Name',
    },
    {
      id: 'guestPhone_number',
      numeric: false,
      disablePadding: false,
      label: 'Guest Phone Number',
    },
    {
      id: 'amount_cents',
      numeric: false,
      disablePadding: false,
      label: 'Amount',
    },
    {
      id: 'currency',
      numeric: false,
      disablePadding: false,
      label: 'Currency',
    },
    {
      id: 'success',
      numeric: false,
      disablePadding: false,
      label: 'Payment Status',
    },
    {
      id: 'failureReason',
      numeric: false,
      disablePadding: false,
      label: 'Details',
    },
    {
      id: 'type',
      numeric: false,
      disablePadding: false,
      label: 'type',
    },

    {
      id: 'createdAt',
      numeric: false,
      disablePadding: false,
      label: 'Creation Date',
    },

    {
      id: 'actions',
      numeric: true,
      disablePadding: false,
      label: '',
    },
  ];
  const dataCells: readonly string[] = [
    'transactionID',
    'guestName',
    'guestPhone_number',
    'amount_cents',
    'currency',
    'success',
    'failureReason',
    'type',
    'createdAt',
  ];

  const api: any = {};
  api[`${listingName}CreateSubscription`] = onCreateTransaction;
  api[`${listingName}HeadCells`] = headCells;
  api[`${listingName}DataCells`] = dataCells;
  api[`${listingName}Fetch`] = fetch;
  api[`${listingName}Get`] = get;
  api[`${listingName}Update`] = update;
  api[`${listingName}Export`] = exportAll;

  api[`${listingName}ChangeListing`] = (listing: Transaction[]) =>
    dispatch(setListing(listing));
  api[`${listingName}ChangeSelected`] = (conceptID: string) =>
    dispatch(setSelected(conceptID));
  api[`${listingName}ClearListing`] = () => dispatch(setListing([]));
  api[`${listingName}ClearNextToken`] = () => dispatch(setNextToken(null));
  api[`${listingName}NextToken`] = nextToken;
  api[`${listingName}Listing`] = transactionsListing;
  return api;
};
export default useTransaction;
